import {
  usePathname,
  useRouter as useExpoRouter
} from "expo-router";
import { useEffect, useMemo } from "react";

import {
  createForwardNavigationIntentGate,
  navigationTargetKey,
  navigationTargetMatchesLocation,
  type ForwardNavigationIntentGate
} from "@/lib/navigationIntent";

const globalScope = globalThis as {
  __recitalForwardNavigationGate?: ForwardNavigationIntentGate;
};
const forwardNavigationGate =
  (globalScope.__recitalForwardNavigationGate ??=
    createForwardNavigationIntentGate());

/**
 * Expo Router with push/navigate protected against rapid duplicate dispatch.
 * Back, replace, dismiss, and setParams retain their native semantics.
 */
export function useAppRouter(): ReturnType<typeof useExpoRouter> {
  const router = useExpoRouter();
  const pathname = usePathname();

  useEffect(() => {
    forwardNavigationGate.observeLocation(pathname);
  }, [pathname]);

  return useMemo(() => {
    const push: typeof router.push = (...args) => {
      if (navigationTargetMatchesLocation(args[0], pathname)) return;
      const target = navigationTargetKey(args[0]);
      if (!forwardNavigationGate.tryBegin(target)) return;
      try {
        router.push(...args);
      } catch (error) {
        forwardNavigationGate.release(target);
        throw error;
      }
    };
    const navigate: typeof router.navigate = (...args) => {
      if (navigationTargetMatchesLocation(args[0], pathname)) return;
      const target = navigationTargetKey(args[0]);
      if (!forwardNavigationGate.tryBegin(target)) return;
      try {
        router.navigate(...args);
      } catch (error) {
        forwardNavigationGate.release(target);
        throw error;
      }
    };

    return { ...router, navigate, push };
  }, [pathname, router]);
}
